// src/views/deviceInformation/ConsumptionRecords.tsx
import { useQuery } from "@tanstack/react-query";
import { useDisclosure } from "@chakra-ui/hooks";
import CustomButton from "components/button";
import CustomPagination from "components/pagination";
import SimpleTable from "components/tables/SimpleTable";
import { useEffect, useState } from "react";
import { MdDelete, MdEdit, MdOutlineFileDownload } from "react-icons/md";
import { MdRemoveRedEye } from "react-icons/md";
import { useParams } from "react-router-dom";
import { reqFunction } from "utils/API";
import { renderUnit } from "utils/CommonFunctions";
import { getDiffrenceInHours, renderTimeJalali } from "utils/TimeUtiles";
import { renderToast } from "utils/globalUtils";
import { CountOption, countSelect } from "variables";
import { TagObject } from "views/counters";
import { TypeObject } from "views/settings/DeviceTypesTable";
import { UserObject } from "views/users/UsersTable";
import { OneDeviceObj } from ".";
import ConsumptionChartFilter from "./ConsumptionChartFilter";
import CustomModal from "components/modals";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import Card from "components/card";
import { CounterStatusRenderer } from "views/settings/CounterStatus";

export type LastConsumptionInfo = {
  RelayStatus: boolean;
  DeviceOnTime: number;
  SignalQuality: number;
  temperature_detail: {
    MCU: number;
    WT?: number | null;
  };
  voltage_detail: {
    RTC: number;
    Backup: number;
    Battery: number;
  };
  FirmwareVersion: string;
  Pressure?: number | null;
};

export type ConsumptionRecordObject = {
  from_previous_record: string;
  to_current_record: string;
  consumption_id: string;
  counter: string;
  log_time: string;
  log_message: string;
  information: LastConsumptionInfo;
  value: number;
  device_value: null | number;
  cumulative_value: number;
  create_time: string;
  water_meters_info: {
    water_meter_serial: string;
    water_meter_name: string;
  };
  type_info: TypeObject;
  tag_info: TagObject;
  all_consumptions: number;
  user_info: UserObject;
  project_info: {
    water_meter_project_id: string;
    water_meter_project_name: string;
  };
};

type RecordsTableData = {
  counterSerial: string;
  dataCounter: string;
  logDate: JSX.Element;
  recordDate: JSX.Element;

  // one cell per metric column
  backupStatus: JSX.Element; // بکاپ
  signalStatus: JSX.Element; // سیگنال
  feedStatus: JSX.Element; // تغذیه
  pressureStatus?: JSX.Element; // فشار (اختیاری)
  deviceTempStatus?: JSX.Element; // دما دستگاه (ادمین)
  waterTempStatus?: JSX.Element; // دما آب (ادمین)

  recordValue: string;
  recordTimeSpan: string;
  consumptionInPeriod: string;
  cumulativeValue: string;
  action: JSX.Element;
};

interface ConsumptionRecordsProps {
  deviceInfo: [OneDeviceObj];
  fromDate: any;
  setFromDate: React.Dispatch<any>;
  tillDate: any;
  setTillDate: React.Dispatch<any>;
}

const ConsumptionRecords = (props: ConsumptionRecordsProps) => {
  const { deviceSerial } = useParams();
  const { deviceInfo, fromDate, setFromDate, tillDate, setTillDate } = props;
  const [excelLoading, setExcelLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [count, setCount] = useState<CountOption>(countSelect[0]);
  const [message, setMessage] = useState<string | null>(null);
  const [selectedRecords, setSelectedRecords] = useState<string[]>([]);

  const AdminPermissions: string[] = JSON.parse(
    window.localStorage.getItem("permissions")
  );

  const {
    data: recordsData,
    isLoading: recordsIsLoading,
    status: recordsStatus,
    refetch: recordsRefetch,
  } = useQuery({
    queryFn: () =>
      reqFunction(
        "watermeters/admin/getAll/consumption",
        {
          page: page,
          count: count.value,
          start_time: fromDate.format("YYYY-M-D"),
          end_time: tillDate.format("YYYY-M-D"),
          water_meters: deviceSerial,
          water_meter_user: null,
          water_meter_project: null,
          water_meter_type: null,
        },
        "post"
      ),
    queryKey: [
      "consumptionRecords",
      deviceSerial,
      page,
      count,
      fromDate.format("YYYY-M-D"),
      tillDate.format("YYYY-MM-D"),
    ],
  });

  const hasPressure =
    Array.isArray(recordsData?.data) &&
    recordsData.data.some(
      (r: ConsumptionRecordObject) =>
        r?.information?.Pressure !== null &&
        r?.information?.Pressure !== undefined
    );

  const hasWaterTemp =
    Array.isArray(recordsData?.data) &&
    recordsData.data.some(
      (r: ConsumptionRecordObject) =>
        r?.information?.temperature_detail?.WT !== null &&
        r?.information?.temperature_detail?.WT !== undefined
    );

  // Liter or Cubic Meter
  const [unitFilter, setUnitFilter] = useState(false);

  // ---------- table header (normal columns) ----------
  let tableHeader = [
    { title: "دیتا کانتر", headerKey: "dataCounter" },
    { title: "زمان ایجاد", headerKey: "recordDate" },
    { title: "زمان دریافت", headerKey: "logDate" },

    // one column per metric:
    { title: "بکاپ", headerKey: "backupStatus" },
    { title: "سیگنال", headerKey: "signalStatus" },
    { title: "تغذیه", headerKey: "feedStatus" },
  ] as { title: string; headerKey: string }[];

  if (hasPressure) {
    tableHeader.push({ title: "فشار", headerKey: "pressureStatus" });
  }
  tableHeader.push({ title: "دما دستگاه", headerKey: "deviceTempStatus" });

  if (hasWaterTemp) {
    tableHeader.push({ title: "دما آب", headerKey: "waterTempStatus" });
  }

  tableHeader.push(
    {
      title: `آخرین مصرف ارسالی (${
        recordsData
          ? renderUnit(
              recordsData[0]?.tag_info?.water_meter_tag_name,
              unitFilter
            )
          : ""
      })`,
      headerKey: "recordValue",
    },
    {
      title: `مصرف در بازه ارسال (${
        recordsData
          ? renderUnit(
              recordsData[0]?.tag_info?.water_meter_tag_name,
              unitFilter
            )
          : ""
      })`,
      headerKey: "consumptionInPeriod",
    },
    {
      title: `مصرف تجمعی (${
        recordsData
          ? renderUnit(
              recordsData[0]?.tag_info?.water_meter_tag_name,
              unitFilter
            )
          : ""
      })`,
      headerKey: "cumulativeValue",
    }
  );

  // special case for برق – unchanged
  if (deviceInfo[0]?.water_meter_tag_info?.water_meter_tag_name === "برق") {
    tableHeader = [
      { title: "زمان ایجاد", headerKey: "recordDate" },
      { title: "مصرف در بازه ارسال", headerKey: "consumptionInPeriod" },
    ];
  }

  if (AdminPermissions.includes("LogDevice")) {
    tableHeader.push({ title: "عملیات", headerKey: "action" });
  }

  const getExcelFile = async () => {
    setExcelLoading(true);
    const response = await reqFunction(
      "watermeters/admin/getOne/csv",
      {
        water_meter_serial: deviceSerial,
        start_time: fromDate.format("YYYY-M-D"),
        end_time: tillDate.format("YYYY-M-D"),
      },
      "post"
    );
    if (response.code === 200) {
      let fileUrl =
        process.env.REACT_APP_SAYAL_API_ENDPOINT_MAIN + response.data.fileurl;
      let link = document.getElementById("download-xlsx-counter");
      link.setAttribute("href", fileUrl);
      link.setAttribute("download", fileUrl);
      link.click();
      setExcelLoading(false);
    } else {
      renderToast(
        response?.farsi_message
          ? response.farsi_message
          : "در دریافت فایل اکسل خطایی رخ داده",
        "err"
      );
      setExcelLoading(false);
    }
  };

  const deleteSelectedRecords = async () => {
    if (selectedRecords.length === 0) {
      renderToast("هیچ رکوردی انتخاب نشده است.", "err");
      return;
    }
    if (!window.confirm("آیا از حذف رکوردهای انتخابی اطمینان دارید؟")) return;

    for (let id of selectedRecords) {
      await reqFunction("watermeters/admin/delete/consumption", {
        mode: "single_consumption",
        time: null,
        consumption_id: id,
        water_meter_serial: deviceSerial,
      });
    }
    renderToast("رکوردهای انتخاب شده با موفقیت حذف شدند.", "success");
    setSelectedRecords([]);
    recordsRefetch();
  };

  const deleteOneRecord = async (consumptionId: string) => {
    if (!window.confirm("آیا از حذف این رکورد اطمینان دارید؟")) return;

    const response = await reqFunction(
      "watermeters/admin/delete/consumption",
      {
        mode: "single_consumption",
        time: null,
        consumption_id: consumptionId,
        water_meter_serial: deviceSerial,
      },
      "post"
    );

    if (response.code === 200) {
      renderToast("رکورد با موفقیت حذف شد.", "success");
      recordsRefetch();
    } else {
      renderToast(response?.farsi_message || "خطا در حذف رکورد", "err");
    }
  };

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const tableData = () => {
    let tableData: RecordsTableData[] = [];

    recordsData?.data?.forEach((record: ConsumptionRecordObject) => {
      const info = record.information;

      tableData.push({
        counterSerial: record.consumption_id,
        dataCounter: record?.counter?.toString(),

        recordDate: renderTimeJalali(record.create_time, "chart"),
        logDate: (() => {
          try {
            return record.log_time ? (
              <>{renderTimeJalali(record.log_time, "chart")}</>
            ) : (
              <></>
            );
          } catch (error) {
            return <></>;
          }
        })(),

        // each column uses the same shapes as before via CounterStatusRenderer + mode
        backupStatus: (
          <CounterStatusRenderer
            status={info}
            isMobile={isMobile}
            mode="battery"
          />
        ),
        signalStatus: (
          <CounterStatusRenderer
            status={info}
            isMobile={isMobile}
            mode="signal"
          />
        ),
        feedStatus: (
          <CounterStatusRenderer
            status={info}
            isMobile={isMobile}
            mode="backup"
          />
        ),
        pressureStatus: hasPressure ? (
          <CounterStatusRenderer
            status={info}
            isMobile={isMobile}
            hasPressure={hasPressure}
            mode="pressure"
          />
        ) : undefined,
        deviceTempStatus: (
          <CounterStatusRenderer
            status={info}
            isMobile={isMobile}
            mode="tempDevice"
          />
        ),
        waterTempStatus: hasWaterTemp ? (
          <CounterStatusRenderer
            status={info}
            isMobile={isMobile}
            mode="tempWater"
          />
        ) : undefined,

        recordValue:
          record.device_value || record.device_value === 0
            ? `${(unitFilter
                ? record.device_value / 1000
                : record.device_value
              ).toLocaleString()}`
            : "__",
        recordTimeSpan: getDiffrenceInHours(
          record.from_previous_record,
          record.create_time
        ),
        consumptionInPeriod:
          record.value || record.value === 0
            ? `${(unitFilter
                ? record.value / 1000
                : record.value
              ).toLocaleString()}`
            : "__",
        cumulativeValue:
          record.cumulative_value || record.cumulative_value === 0
            ? `${(unitFilter
                ? record.cumulative_value / 1000
                : record.cumulative_value
              ).toLocaleString()}`
            : "__",
        action: renderLogAction(record.log_message, record),
      });
    });
    return tableData;
  };

  const renderLogAction = (
    message: string,
    record?: ConsumptionRecordObject
  ) => {
    return (
      <div className="flex items-center justify-center gap-2">
        <CustomButton
          onClick={() => renderMessage(message)}
          icon={<MdRemoveRedEye />}
          color="blue"
          extra="!p-2"
        />
        {AdminPermissions.includes("Joker") && (
          <>
            <CustomButton
              onClick={() => openEditModal(record!)}
              icon={<MdEdit />}
              color="orange"
              extra="!p-2"
            />
            <CustomButton
              onClick={() => deleteOneRecord(record!.consumption_id)}
              icon={<MdDelete />}
              color="red"
              extra="!p-2"
            />
          </>
        )}
      </div>
    );
  };

  const {
    isOpen: isLogOpen,
    onOpen: onLogOpen,
    onClose: onLogClose,
  } = useDisclosure();

  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();

  const [editRecord, setEditRecord] = useState<ConsumptionRecordObject | null>(
    null
  );
  const [editForm, setEditForm] = useState({
    information: {} as LastConsumptionInfo,
    value: "" as string,
    cumulative_value: "" as string,
    device_value: "" as string,
  });

  const renderMessage = (message: string) => {
    if (!message.trim()) {
      setMessage(message);
      onLogOpen();
      return;
    }

    let correctMessage = message
      .replace(/\s/g, "")
      .replace(/\?/g, "")
      .replace(/\bnan\b/g, "null");

    let parsed: any = null;
    try {
      parsed = JSON.parse(correctMessage);
    } catch (e) {
      parsed = null;
    }

    setMessage(parsed ? parsed : message);
    onLogOpen();
  };

  const openEditModal = (record: ConsumptionRecordObject) => {
    setEditRecord(record);
    setEditForm({
      information: record.information,
      value:
        record.value === null || record.value === undefined
          ? ""
          : String(record.value),
      cumulative_value:
        record.cumulative_value === null ||
        record.cumulative_value === undefined
          ? ""
          : String(record.cumulative_value),
      device_value:
        record.device_value === null || record.device_value === undefined
          ? ""
          : String(record.device_value),
    });
    onEditOpen();
  };

  const handleEditChange = (field: string, value: any) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const saveEditRecord = async () => {
    if (!editRecord) return;

    const toNum = (s: string) => {
      if (s === "" || s === "-") return null;
      const n = parseFloat(s);
      return Number.isFinite(n) ? n : null;
    };

    const payload = {
      water_meter_serial: deviceSerial,
      consumption_id: editRecord.consumption_id,
      value: toNum(editForm.value),
      information: editForm.information,
      create_time: editRecord.create_time,
      from_previous_record: editRecord.from_previous_record,
      to_current_record: editRecord.to_current_record,
      device_value: toNum(editForm.device_value),
      cumulative_value: toNum(editForm.cumulative_value),
    };

    const response = await reqFunction(
      "watermeters/admin/edit/consumption",
      payload,
      "post"
    );

    if (response.code === 200) {
      renderToast("رکورد با موفقیت ویرایش شد.", "success");
      recordsRefetch();
      onEditClose();
    } else {
      renderToast(response?.farsi_message || "خطا در ویرایش رکورد", "err");
    }
  };

  return (
    <>
      <div className="projects-overview pt-4">
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between md:gap-4">
          {/* Right section (project/device info) */}
          <div className="space-y-1 text-sm font-bold text-navy-700 md:w-1/3 dark:text-white">
            <div>
              <span className="text-gray-500 dark:text-gray-300">
                نام پروژه:{" "}
              </span>
              <span>
                {deviceInfo[0]?.water_meter_project_info?.project_name}
              </span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-300">
                نام دستگاه:{" "}
              </span>
              <span>{deviceInfo[0].water_meter_name}</span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-300">
                سریال دستگاه:{" "}
              </span>
              <span>{deviceInfo[0].water_meter_serial}</span>
            </div>
          </div>

          {/* Middle section (chart filter) */}
          <div className="flex justify-center md:flex-1">
            <ConsumptionChartFilter
              fromDate={fromDate}
              setFromDate={setFromDate}
              tillDate={tillDate}
              setTillDate={setTillDate}
              update={recordsRefetch}
              chartTag={{
                label: deviceInfo[0].water_meter_tag_info.water_meter_tag_name,
                value: deviceInfo[0].water_meter_tag_info.water_meter_tag_id,
              }}
              tab="records"
            />
          </div>

          {/* Left section (buttons) */}
          <div className="flex flex-wrap items-center justify-end gap-2 md:w-1/3">
            {AdminPermissions.includes("Joker") && (
              <CustomButton
                text="حذف انتخاب شده‌ها"
                onClick={deleteSelectedRecords}
                icon={<MdDelete />}
                color="red"
                extra="text-sm"
                isDisable={selectedRecords.length === 0}
              />
            )}
            <CustomButton
              text="تغییر واحد"
              onClick={() => setUnitFilter(!unitFilter)}
              color="blue"
              extra="text-sm"
            />
            <CustomButton
              text="دریافت اکسل"
              onClick={() => getExcelFile()}
              icon={<MdOutlineFileDownload />}
              color="green"
              extra="text-sm"
              isDisable={excelLoading}
            />
          </div>
        </div>

        {!recordsIsLoading && recordsStatus === "success" ? (
          <div className="table-container mx-auto  w-full ">
            <CustomModal
              isOpen={isLogOpen}
              onClose={onLogClose}
              title={"پیام"}
              modalType="confirmation"
              information={
                <SyntaxHighlighter
                  language="json"
                  style={vscDarkPlus}
                  customStyle={{ whiteSpace: "pre-wrap !important" }}
                >
                  {typeof message === "string"
                    ? message
                    : JSON.stringify(message, null, 2)}
                </SyntaxHighlighter>
              }
            />
            <CustomModal
              isOpen={isEditOpen}
              onClose={onEditClose}
              title="ویرایش رکورد"
              modalType="confirmation"
              information={
                <div className="space-y-3">
                  <div>
                    <label>مصرف در بازه ارسال</label>
                    <input
                      type="number"
                      inputMode="decimal"
                      step="any"
                      value={editForm.value}
                      onChange={(e) =>
                        setEditForm((p) => ({ ...p, value: e.target.value }))
                      }
                      className="w-full rounded border p-2"
                    />
                  </div>

                  <div>
                    <label>مصرف تجمعی</label>
                    <input
                      type="number"
                      inputMode="decimal"
                      step="any"
                      value={editForm.cumulative_value}
                      onChange={(e) =>
                        setEditForm((p) => ({
                          ...p,
                          cumulative_value: e.target.value,
                        }))
                      }
                      className="w-full rounded border p-2"
                    />
                  </div>

                  <div>
                    <label>آخرین مصرف ارسالی</label>
                    <input
                      type="number"
                      inputMode="decimal"
                      step="any"
                      value={editForm.device_value}
                      onChange={(e) =>
                        setEditForm((p) => ({
                          ...p,
                          device_value: e.target.value,
                        }))
                      }
                      className="w-full rounded border p-2"
                    />
                  </div>

                  <div>
                    <label>اطلاعات وضعیت کنتور</label>
                    <textarea
                      value={JSON.stringify(editForm.information, null, 2)}
                      onChange={(e) =>
                        handleEditChange(
                          "information",
                          JSON.parse(e.target.value || "{}")
                        )
                      }
                      className="w-full rounded border p-2 font-mono text-sm"
                      rows={6}
                    />
                  </div>
                  <CustomButton
                    text="ذخیره تغییرات"
                    onClick={saveEditRecord}
                    color="green"
                  />
                </div>
              }
            />

            <Card extra="!h-[55vh] mt-4">
              <div className="table-container mx-auto w-full">
                <SimpleTable
                  header={tableHeader}
                  data={tableData()}
                  hasIndex={true}
                  page={page}
                  count={count.value}
                  isCheckTable={AdminPermissions.includes("Joker")}
                  selectedItems={selectedRecords}
                  setSelectedItems={setSelectedRecords}
                  extra="h-[50vh] text-xs"
                />
              </div>
            </Card>
            <div className="pagination ">
              <CustomPagination
                page={page}
                pageSize={count}
                totalData={
                  recordsData?.data?.length > 0
                    ? recordsData.data[0].all_consumptions
                    : 0
                }
                changePageFunc={setPage}
                changeCount={setCount}
              />
            </div>
            <a
              id="download-xlsx-counter"
              href="/"
              rel="noopener noreferrer"
              style={{ display: "none" }}
            >
              exel file
            </a>
          </div>
        ) : (
          <Card extra="!h-[55vh] mt-4 flex flex-col items-center justify-center">
            <div className="table-container mx-auto w-full">
              <SimpleTable
                header={tableHeader}
                data={Array.from({ length: 8 }).map((_, idx) => {
                  let skeletonRow: any = {};
                  tableHeader.forEach((col) => {
                    skeletonRow[col.headerKey] = (
                      <div className="w-full">
                        <div className="h-4 w-3/4 animate-pulse rounded bg-gray-300 dark:bg-gray-700"></div>
                      </div>
                    );
                  });
                  skeletonRow["counterSerial"] = `skeleton-${idx}`;
                  return skeletonRow;
                })}
                hasIndex={true}
                page={page}
                count={count.value}
                isCheckTable={false}
                selectedItems={[]}
                setSelectedItems={() => {}}
                extra="h-[50vh] text-xs opacity-60 pointer-events-none"
              />
            </div>
            <div className="mt-4">
              <svg
                className="h-8 w-8 animate-spin text-blue-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8z"
                ></path>
              </svg>
            </div>
          </Card>
        )}
      </div>
    </>
  );
};

export default ConsumptionRecords;
